// Shared Streamdown render configuration. It lives in the neutral UI zone
// rather than in a domain package because its readers span four owners — the
// Evidence chat panel, the share-evidence modal, the Projects timeline and
// sub-agent answers, and the coding-case Ask Heidi thread — so no single domain
// owns it, and nothing here carries state or a component of its own.
import { defaultSchema } from 'hast-util-sanitize';
import rehypeSanitize from 'rehype-sanitize';
import { defaultRehypePlugins, type StreamdownProps } from 'streamdown';

/**
 * Custom sanitize schema for Streamdown v2.
 *
 * Streamdown v2 introduced rehype-sanitize by default, which strips unknown HTML elements.
 * This schema extends the default to allow our custom elements:
 * - <cite-badge data-idx="..."> - inline citation badges
 * - <keypoints> - key points section wrapper
 * - <usernotes data-raw="..."> - user notes block with base64-encoded content
 * - <heidi-button href="..."> - inline call-to-action button (URL allowlisted)
 * - <safetycallout> - prominent clinical safety / drug-interaction callout (EV-023)
 * - <keysafetynotes> - trailing Key Safety Notes summary (EVI-1627). Hyphen-free,
 *     all-lowercase name so HTML parsers and Streamdown's block parser agree.
 * - <img-figure data-token="..."> - Evidence sub-agent inline image anchor
 * - <project data-id="..."> - Projects prior-thread citation chip (title as children)
 * - <chat …> - legacy cite tag (still allowed so old transcripts render)
 * - <ref data-kind data-id data-session-id> - Work chat object reference (hint as children)
 * - <source-cite data-idx="..."> - source-grounded routine citation chip
 */
export const customSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'cite-badge',
    'keypoints',
    'usernotes',
    'evidence-image',
    'heidi-button',
    'safetycallout',
    'keysafetynotes',
    'img-figure',
    'project',
    'chat',
    'ref',
    'source-cite',
  ],
  attributes: {
    ...defaultSchema.attributes,
    // Note: hast-util-sanitize uses camelCase for data attributes
    'cite-badge': ['dataIdx'],
    keypoints: [],
    usernotes: ['dataRaw'],
    'evidence-image': ['dataN'],
    // The href value is further validated against an allowlist at render time
    // by the HeidiButton component before any navigation happens.
    'heidi-button': ['href'],
    safetycallout: [],
    keysafetynotes: [],
    'img-figure': ['dataToken', 'dataN'],
    // data-id (not HTML id): hast-util-sanitize rewrites ``id`` to
    // ``user-content-…``, which breaks chip navigation.
    project: ['dataId'],
    // Legacy ``<chat>`` — keep so older transcripts still render.
    chat: ['dataProjectId', 'id'],
    ref: ['dataKind', 'dataId', 'dataSessionId'],
    'source-cite': ['dataIdx'],
  },
};

const streamdownHarden = defaultRehypePlugins?.harden;
const [hardenPlugin, hardenOptions] = Array.isArray(streamdownHarden)
  ? streamdownHarden
  : [];
const hardenEntry = hardenPlugin
  ? [
      [
        hardenPlugin,
        { ...hardenOptions, allowedImagePrefixes: [], imageBlockPolicy: 'remove' },
      ],
    ]
  : [];

/**
 * Custom rehype plugins array for Streamdown.
 * Replaces the default rehype-sanitize with our custom schema.
 *
 * Order matters:
 * 1. raw - parses raw HTML in markdown
 * 2. sanitize - filters allowed elements/attributes
 * 3. harden - security hardening
 */
export const streamdownRehypePlugins = [
  defaultRehypePlugins?.raw,
  [rehypeSanitize, customSanitizeSchema],
  ...hardenEntry,
].filter(Boolean) as NonNullable<StreamdownProps['rehypePlugins']>;

type HastNode = { type: string; tagName?: string; children?: HastNode[] };

const renameImages = (node: HastNode): void => {
  if (node.type === 'element' && node.tagName === 'img') node.tagName = 'image-ref';
  node.children?.forEach(renameImages);
};

/** Rehype plugin: every sanitized `<img>` becomes an `<image-ref>` with the same properties. */
export const rehypeImageRefs = () => renameImages;

/**
 * `streamdownRehypePlugins` for a preview of content that is sent with its
 * images, such as the Work email-approval card. Each `<img>` becomes an
 * `<image-ref>` between sanitize and harden, so harden never removes it and the
 * browser never fetches it; the renderer maps `image-ref` to a label from `alt`.
 */
export const streamdownRehypePluginsWithImageRefs = [
  defaultRehypePlugins?.raw,
  [rehypeSanitize, customSanitizeSchema],
  rehypeImageRefs,
  ...hardenEntry,
].filter(Boolean) as NonNullable<StreamdownProps['rehypePlugins']>;
